import { ReactNode } from 'react';
import {
  Button,
  ButtonGroup,
  ButtonProps,
  Popover as ChakraPopover,
  PopoverArrow,
  PopoverBody,
  PopoverBodyProps,
  PopoverCloseButton,
  PopoverContent,
  PopoverContentProps,
  PopoverFooter,
  PopoverHeader,
  PopoverHeaderProps,
  PopoverProps as ChakraPopoverProps,
  PopoverTrigger,
  useDisclosure,
  useMultiStyleConfig,
} from '@chakra-ui/react';

export interface PopoverProps extends ChakraPopoverProps {
  title?: ReactNode;
  message?: ReactNode;
  showArrow?: boolean;
  showCloseIcon?: boolean;
  okText?: ReactNode;
  okProps?: ButtonProps;
  cancelText?: ReactNode;
  cancelProps?: ButtonProps;
  titleProps?: PopoverHeaderProps;
  contentProps?: PopoverContentProps;
  messageProps?: PopoverBodyProps;
  onOk?: () => void;
  triggerButtonProps?: ButtonProps;
  showFooter?: boolean;
}

export const Popover = (props: PopoverProps) => {
  const {
    title,
    message,
    showArrow = true,
    showCloseIcon = false,
    okText = 'Ok',
    okProps,
    cancelText = 'Close',
    cancelProps,
    titleProps,
    contentProps,
    messageProps,
    onOk,
    triggerButtonProps,
    showFooter = true,
    children,
    ...rest
  } = props;
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const handleClick = () => {
    onOk && onOk();
    onClose();
  };
  const styles = useMultiStyleConfig(`Popover`, {});

  return (
    <ChakraPopover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
      {...rest}
    >
      <PopoverTrigger>
        <Button onClick={onToggle} {...triggerButtonProps}>
          {children}
        </Button>
      </PopoverTrigger>
      <PopoverContent sx={{ ...styles.PopoverContent }} {...contentProps}>
        {title && (
          <PopoverHeader sx={{ ...styles.PopoverHeader }} {...titleProps}>
            {title}
          </PopoverHeader>
        )}
        {showArrow && <PopoverArrow />}
        {showCloseIcon && <PopoverCloseButton />}
        {message && <PopoverBody {...messageProps}>{message}</PopoverBody>}
        {showFooter && (
          <PopoverFooter sx={{ ...styles.PopoverFooter }}>
            <ButtonGroup size='sm'>
              <Button
                colorScheme='white'
                variant='outline'
                onClick={onClose}
                {...cancelProps}
              >
                {cancelText}
              </Button>
              <Button onClick={handleClick} {...okProps}>
                {okText}
              </Button>
            </ButtonGroup>
          </PopoverFooter>
        )}
      </PopoverContent>
    </ChakraPopover>
  );
};
